import { Head, Link } from '@inertiajs/react';
import {
  ArrowLeft,
  ExternalLink,
  MapPin,
  MessageCircle,
  Phone,
  Users,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { AvatarCircle } from '@/components/admin/avatar-circle';
import { EmailsCard } from '@/components/admin/emails-card';
import { githubUsername } from '@/components/admin/github-username';
import { Github, Linkedin, XLogo } from '@/components/icons';
import { Linkified } from '@/components/linkified';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import type { AdminHackersShow } from '@/types';

function formatDateTime(timestamp: string) {
  return new Date(timestamp).toLocaleString('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function Field({
  label,
  value,
}: {
  label: string;
  value: ReactNode;
}) {
  if (!value) return null;

  return (
    <div>
      <div className="mb-1 font-medium text-muted-foreground text-xs uppercase tracking-wide">
        {label}
      </div>
      <div className="text-sm">{value}</div>
    </div>
  );
}

const SUBMISSION_STATUS_LABELS: Record<string, string> = {
  received: 'Received',
  priority_waiting: 'Priority waiting',
  asking_self_finance_trip: 'Asking self-finance trip',
  approved: 'Approved',
  onboarding_request: 'Onboarding requested',
  onboarding_expired: 'Onboarding expired',
  onboarding_complete: 'Onboarding complete',
  rejected: 'Rejected',
  waiting_list: 'Waiting list',
  withdrawn: 'Withdrawn',
  archived: 'Archived',
  draft: 'Draft',
};

export default function Show({
  hacker,
  profile,
  emails,
  eventSlug,
  title,
}: AdminHackersShow) {
  return (
    <>
      <Head title={title} />
      <div className="container mx-auto max-w-4xl space-y-6 py-8">
        <Button variant="outline" size="sm" asChild>
          <Link href={`/admin/${eventSlug}/dashboard`}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Link>
        </Button>

        <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
          <AvatarCircle src={hacker.avatarUrl} name={hacker.fullName} size="lg" />
          <div>
            <h1 className="font-bold text-3xl tracking-tight">
              {hacker.fullName}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <a
                href={`https://github.com/${githubUsername(hacker.github)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-blue-600 text-sm hover:text-blue-800"
              >
                <Github className="h-3.5 w-3.5" />@{githubUsername(hacker.github)}
                <ExternalLink className="h-3 w-3" />
              </a>
              <Badge
                variant={profile.memberStatus === 'active' ? 'secondary' : 'outline'}
              >
                {profile.memberStatus === 'active' ? 'Active' : 'Withdrawn'}
              </Badge>
              {profile.onboardCompleteAt && (
                <Badge variant="outline">Onboarded</Badge>
              )}
            </div>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Profile</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Email" value={profile.email} />
            <Field label="Country" value={profile.country} />
            <Field
              label="Emergency contact"
              value={
                profile.emergencyContactName && (
                  <span className="flex items-center gap-1.5">
                    {profile.emergencyContactName}
                    {profile.emergencyContactPhone && (
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <Phone className="h-3 w-3" />
                        {profile.emergencyContactPhone}
                      </span>
                    )}
                  </span>
                )
              }
            />
            <Field
              label="Discord"
              value={
                profile.discordUsername && (
                  <span className="flex items-center gap-1.5">
                    <MessageCircle className="h-3.5 w-3.5" />
                    {profile.discordUsername}
                  </span>
                )
              }
            />
            <Field
              label="LinkedIn"
              value={
                profile.linkedin && (
                  <a
                    href={profile.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-blue-600 hover:text-blue-800"
                  >
                    <Linkedin className="h-3.5 w-3.5" />
                    Profile
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )
              }
            />
            <Field
              label="X / Twitter"
              value={
                profile.x && (
                  <a
                    href={profile.x}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-blue-600 hover:text-blue-800"
                  >
                    <XLogo className="h-3.5 w-3.5" />
                    Profile
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )
              }
            />
            <Field label="Shirt size" value={profile.shirtSize} />
            <Field label="Shoe size" value={profile.shoeSize} />
            <Field label="Diet" value={profile.diet} />
            <Field
              label="Roles"
              value={
                profile.roles && profile.roles.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {profile.roles.map((role) => (
                      <Badge key={role} variant="outline">
                        {role}
                      </Badge>
                    ))}
                  </div>
                )
              }
            />
            <div className="sm:col-span-2">
              <Field
                label="Bio"
                value={profile.bio && <Linkified text={profile.bio} />}
              />
            </div>
            <div className="sm:col-span-2">
              <Field
                label="Education"
                value={
                  profile.education && <Linkified text={profile.education} />
                }
              />
            </div>
            {profile.isVeteran && (
              <div className="sm:col-span-2">
                <Field
                  label="Previous hackathons"
                  value={profile.previousHackathons}
                />
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Submission</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">
                    {SUBMISSION_STATUS_LABELS[profile.submission.status] ??
                      profile.submission.status}
                  </span>
                  <Badge variant="outline">{profile.submission.modality}</Badge>
                </div>
                <p className="text-muted-foreground text-sm">
                  Submitted {formatDateTime(profile.submission.submittedAt)}
                </p>
              </div>
              <Button variant="outline" size="sm" asChild>
                <Link
                  href={`/admin/${eventSlug}/submissions/${profile.submission.id}`}
                >
                  View Submission
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Team</CardTitle>
          </CardHeader>
          <CardContent>
            {profile.team ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Link
                    href={`/admin/${eventSlug}/teams/${profile.team.slug}`}
                    className="flex items-center gap-2 font-medium text-blue-600 hover:text-blue-800"
                  >
                    <Users className="h-4 w-4" />
                    {profile.team.slug}
                  </Link>
                  {profile.team.tableNumber && (
                    <Badge variant="outline">
                      <MapPin className="mr-1 h-3 w-3" />
                      Table {profile.team.tableNumber}
                    </Badge>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field
                    label="Track"
                    value={
                      profile.team.track && (
                        <Link
                          href={`/admin/${eventSlug}/tracks/${profile.team.track.slug}`}
                          className="text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {profile.team.track.name}
                        </Link>
                      )
                    }
                  />
                  <Field
                    label="Mentor"
                    value={
                      profile.team.mentor && (
                        <span className="flex items-center gap-2">
                          <AvatarCircle
                            src={profile.team.mentor.avatarUrl}
                            name={profile.team.mentor.fullName}
                            size="sm"
                          />
                          {profile.team.mentor.fullName}
                        </span>
                      )
                    }
                  />
                </div>
              </div>
            ) : (
              <div className="py-4 text-center text-muted-foreground">
                <p>Not on a team yet.</p>
              </div>
            )}
          </CardContent>
        </Card>

        <EmailsCard
          emails={emails}
          emptyMessage="No emails sent to this hacker this edition"
          showRecipient={false}
        />
      </div>
    </>
  );
}
